import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Users, DollarSign, Phone, Target, Trophy, TrendingUp, TrendingDown,
  FileText, BarChart3, PieChart, Activity, PhoneOff, PhoneMissed,
  ThumbsDown, Clock, Brain, Download, Bell, AlertCircle, X,
  Calendar, ArrowUp, ArrowDown, Gauge, CheckCircle
} from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { ScrollArea } from "./ui/scroll-area";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart as RePieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#f97316'];

// Helper to build query string
const buildDateQuery = (dateFrom, dateTo) => {
  if (dateFrom && dateTo) {
    return `?date_from=${dateFrom}&date_to=${dateTo}`;
  }
  return '';
};

// Charts Tab Component
export const ChartsTab = ({ dateFrom, dateTo }) => {
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrends = async () => {
      setLoading(true);
      try {
        const query = buildDateQuery(dateFrom, dateTo);
        const res = await axios.get(`${API}/analytics/trends${query}`);
        setTrends(res.data);
      } catch (error) {
        toast.error('Trend verileri yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    fetchTrends();
  }, [dateFrom, dateTo]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}>
          <BarChart3 size={48} className="text-purple-400" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Daily Calls Trend */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp size={18} className="text-emerald-400" />
            Günlük Arama Trendi
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={trends?.daily_calls || []}>
              <defs>
                <linearGradient id="colorCalls" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="date" stroke="#71717a" fontSize={12} tickFormatter={(v) => v?.slice(5)} />
              <YAxis stroke="#71717a" fontSize={12} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                labelStyle={{ color: '#fff' }}
              />
              <Area type="monotone" dataKey="calls" stroke="#10b981" fill="url(#colorCalls)" name="Toplam Arama" />
              <Line type="monotone" dataKey="successful" stroke="#3b82f6" strokeWidth={2} name="Başarılı" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Daily Sales & Revenue */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign size={18} className="text-amber-400" />
            Günlük Satış & Ciro
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={trends?.daily_sales || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="date" stroke="#71717a" fontSize={12} tickFormatter={(v) => v?.slice(5)} />
              <YAxis yAxisId="left" stroke="#71717a" fontSize={12} />
              <YAxis yAxisId="right" orientation="right" stroke="#71717a" fontSize={12} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                formatter={(value, name) => [name === 'revenue' ? `₺${value.toLocaleString()}` : value, name === 'revenue' ? 'Ciro' : 'Satış']}
              />
              <Bar yAxisId="left" dataKey="sales" fill="#f59e0b" name="Satış" radius={[4, 4, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#8b5cf6" strokeWidth={2} name="Ciro" dot={false} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Hourly Heatmap */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock size={18} className="text-cyan-400" />
            Saat Bazlı Performans (En Verimli Arama Saatleri)
          </CardTitle>
          <CardDescription>Başarı oranına göre renklendirme</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-12 gap-2">
            {trends?.hourly_distribution?.map((hour, i) => {
              const intensity = Math.min(hour.success_rate / 10, 1);
              return (
                <div
                  key={i}
                  className="aspect-square rounded-lg flex flex-col items-center justify-center text-xs"
                  style={{
                    backgroundColor: `rgba(16, 185, 129, ${intensity})`,
                    border: '1px solid rgba(39, 39, 42, 0.5)'
                  }}
                  title={`Saat ${hour.hour}: ${hour.calls} arama, %${hour.success_rate} başarı`}
                >
                  <span className="font-bold text-slate-800">{hour.hour}</span>
                  <span className="text-slate-700 text-[10px]">{hour.calls}</span>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-center gap-4 mt-4 text-sm text-slate-500">
            <span className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-emerald-500/20" /> Düşük
            </span>
            <span className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-emerald-500/50" /> Orta
            </span>
            <span className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-emerald-500" /> Yüksek
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Funnel Tab Component
export const FunnelTab = ({ dateFrom, dateTo }) => {
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFunnel = async () => {
      setLoading(true);
      try {
        const query = buildDateQuery(dateFrom, dateTo);
        const res = await axios.get(`${API}/analytics/funnel${query}`);
        setFunnel(res.data);
      } catch (error) {
        toast.error('Funnel verileri yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    fetchFunnel();
  }, [dateFrom, dateTo]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}>
          <BarChart3 size={48} className="text-purple-400" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Conversion Funnel */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target size={18} className="text-purple-400" />
            Dönüşüm Hunisi
          </CardTitle>
          <CardDescription>Müşteri yolculuğu ve kayıp noktaları</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {funnel?.funnel?.map((stage, i) => (
              <div key={i} className="relative">
                <div 
                  className="h-16 rounded-lg flex items-center justify-between px-6 transition-all"
                  style={{ 
                    backgroundColor: stage.color + '20',
                    borderLeft: `4px solid ${stage.color}`,
                    width: `${Math.max(stage.percentage, 20)}%`
                  }}
                >
                  <div>
                    <p className="font-semibold text-slate-800">{stage.stage}</p>
                    <p className="text-sm text-slate-500">{stage.count.toLocaleString()} kişi</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-slate-800">%{stage.percentage}</p>
                    {stage.drop_rate > 0 && (
                      <p className="text-xs text-rose-400">↓ %{stage.drop_rate} kayıp</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Loss Reasons */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingDown size={18} className="text-rose-400" />
            Kayıp Nedenleri
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={funnel?.loss_reasons || []} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis type="number" stroke="#71717a" />
              <YAxis dataKey="reason" type="category" stroke="#71717a" width={100} fontSize={12} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                formatter={(value) => [value.toLocaleString(), 'Adet']}
              />
              <Bar dataKey="count" fill="#ef4444" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-900/10 border-emerald-500/20">
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-emerald-400">{funnel?.total_conversions?.toLocaleString()}</p>
            <p className="text-sm text-slate-500 mt-1">Toplam Dönüşüm</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-900/10 border-blue-500/20">
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-blue-400">%{funnel?.conversion_rate}</p>
            <p className="text-sm text-slate-500 mt-1">Dönüşüm Oranı</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-900/10 border-amber-500/20">
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-amber-400">{funnel?.total_calls?.toLocaleString()}</p>
            <p className="text-sm text-slate-500 mt-1">Toplam Arama</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Agent Comparison Tab
export const AgentComparisonTab = ({ dateFrom, dateTo }) => {
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchComparison = async () => {
      setLoading(true);
      try {
        const query = buildDateQuery(dateFrom, dateTo);
        const res = await axios.get(`${API}/analytics/agent-comparison${query}`);
        setComparison(res.data);
      } catch (error) {
        toast.error('Karşılaştırma verileri yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    fetchComparison();
  }, [dateFrom, dateTo]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}>
          <BarChart3 size={48} className="text-purple-400" />
        </motion.div>
      </div>
    );
  }

  const ScoreBar = ({ score, max = 100 }) => (
    <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
      <div 
        className="h-full rounded-full transition-all"
        style={{ 
          width: `${score}%`,
          backgroundColor: score > 70 ? '#10b981' : score > 40 ? '#f59e0b' : '#ef4444'
        }}
      />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Top 3 Performers */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy size={18} className="text-amber-400" />
            En İyi Performans
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {comparison?.best_performers?.map((agent, i) => (
              <div 
                key={i}
                className={`p-4 rounded-xl border ${
                  i === 0 ? 'bg-amber-500/10 border-amber-500/30' :
                  i === 1 ? 'bg-slate-200/50 border-slate-300' :
                  'bg-orange-900/10 border-orange-900/30'
                }`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    i === 0 ? 'bg-amber-500 text-black' :
                    i === 1 ? 'bg-slate-400 text-black' :
                    'bg-orange-700 text-slate-800'
                  }`}>
                    {i + 1}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">{agent.name}</p>
                    <p className="text-xs text-slate-500">Skor: {agent.performance_score}</p>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Arama</span>
                    <span className="text-slate-800">{agent.total_calls.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Kayıt OK</span>
                    <span className="text-emerald-400">{agent.kayit_ok}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Dönüşüm</span>
                    <span className="text-blue-400">%{agent.conversion_rate}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* All Agents Table */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gauge size={18} className="text-purple-400" />
            Performans Skorları
          </CardTitle>
          <CardDescription>Dönüşüm (%40) + Hacim (%20) + Tahsilat (%20) + Düşük Red (%20)</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            <div className="space-y-3">
              {comparison?.all_agents?.map((agent, i) => (
                <div key={i} className="p-4 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        agent.rank <= 3 ? 'bg-amber-500 text-black' : 'bg-slate-300 text-slate-700'
                      }`}>
                        {agent.rank}
                      </span>
                      <span className="font-medium text-slate-800">{agent.name}</span>
                    </div>
                    <span className={`text-2xl font-bold ${
                      agent.performance_score > 70 ? 'text-emerald-400' :
                      agent.performance_score > 40 ? 'text-amber-400' : 'text-rose-400'
                    }`}>
                      {agent.performance_score}
                    </span>
                  </div>
                  <ScoreBar score={agent.performance_score} />
                  <div className="grid grid-cols-4 gap-2 mt-3 text-xs">
                    <div className="text-center p-1 rounded bg-slate-50">
                      <p className="text-emerald-400">{agent.score_breakdown?.conversion}</p>
                      <p className="text-slate-500">Dönüşüm</p>
                    </div>
                    <div className="text-center p-1 rounded bg-slate-50">
                      <p className="text-blue-400">{agent.score_breakdown?.volume}</p>
                      <p className="text-slate-500">Hacim</p>
                    </div>
                    <div className="text-center p-1 rounded bg-slate-50">
                      <p className="text-amber-400">{agent.score_breakdown?.collection}</p>
                      <p className="text-slate-500">Tahsilat</p>
                    </div>
                    <div className="text-center p-1 rounded bg-slate-50">
                      <p className="text-purple-400">{agent.score_breakdown?.low_rejection}</p>
                      <p className="text-slate-500">D. Red</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Average Score */}
      <Card className="bg-gradient-to-br from-purple-500/10 to-purple-900/10 border-purple-500/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center gap-4">
            <Gauge size={48} className="text-purple-400" />
            <div>
              <p className="text-4xl font-bold text-slate-800">{comparison?.average_score}</p>
              <p className="text-slate-500">Ortalama Ekip Skoru</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Predictions Tab
export const PredictionsTab = ({ dateFrom, dateTo }) => {
  const [predictions, setPredictions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPredictions = async () => {
      setLoading(true);
      try {
        const query = buildDateQuery(dateFrom, dateTo);
        const res = await axios.get(`${API}/analytics/predictions${query}`);
        setPredictions(res.data);
      } catch (error) {
        toast.error('Tahmin verileri yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    fetchPredictions();
  }, [dateFrom, dateTo]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}>
          <BarChart3 size={48} className="text-purple-400" />
        </motion.div>
      </div>
    );
  }

  const ProgressRing = ({ progress, onTrack }) => (
    <div className="relative w-24 h-24">
      <svg className="w-full h-full transform -rotate-90">
        <circle cx="48" cy="48" r="40" stroke="#27272a" strokeWidth="8" fill="none" />
        <circle 
          cx="48" cy="48" r="40" 
          stroke={onTrack ? '#10b981' : '#ef4444'} 
          strokeWidth="8" 
          fill="none"
          strokeDasharray={251.2}
          strokeDashoffset={251.2 - (251.2 * Math.min(progress, 100)) / 100}
          strokeLinecap="round"
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-xl font-bold text-slate-800">
        %{Math.min(progress, 100)}
      </span>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Progress Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Sales Progress */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-center">Satış İlerlemesi</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            <ProgressRing progress={predictions?.progress_percentage?.sales} onTrack={predictions?.on_track?.sales} />
            <div className="mt-4 text-center">
              <p className="text-2xl font-bold text-slate-800">
                {predictions?.current_month?.sales} / {predictions?.targets?.sales}
              </p>
              <p className={`text-sm flex items-center justify-center gap-1 ${predictions?.on_track?.sales ? 'text-emerald-400' : 'text-rose-400'}`}>
                {predictions?.on_track?.sales ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                Tahmini: {predictions?.predictions?.sales}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Revenue Progress */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-center">Ciro İlerlemesi</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            <ProgressRing progress={predictions?.progress_percentage?.revenue} onTrack={predictions?.on_track?.revenue} />
            <div className="mt-4 text-center">
              <p className="text-2xl font-bold text-slate-800">
                ₺{predictions?.current_month?.revenue?.toLocaleString()}
              </p>
              <p className="text-sm text-slate-500">
                Hedef: ₺{predictions?.targets?.revenue?.toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Calls Progress */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-center">Arama İlerlemesi</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            <ProgressRing progress={predictions?.progress_percentage?.calls} onTrack={predictions?.on_track?.calls} />
            <div className="mt-4 text-center">
              <p className="text-2xl font-bold text-slate-800">
                {predictions?.current_month?.calls?.toLocaleString()}
              </p>
              <p className="text-sm text-slate-500">
                Hedef: {predictions?.targets?.calls?.toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Daily Requirements */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar size={18} className="text-amber-400" />
            Hedefe Ulaşmak İçin Gereken Günlük Performans
          </CardTitle>
          <CardDescription>{predictions?.days_remaining} gün kaldı</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-6">
            <div className="p-4 rounded-xl bg-slate-100">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Şu anki günlük ortalama</span>
                <span className="text-emerald-400 font-bold">{predictions?.daily_averages?.sales} satış</span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-slate-500">Gereken günlük satış</span>
                <span className={`font-bold ${predictions?.required_daily?.sales > predictions?.daily_averages?.sales ? 'text-rose-400' : 'text-emerald-400'}`}>
                  {predictions?.required_daily?.sales?.toFixed(1)} satış
                </span>
              </div>
            </div>
            <div className="p-4 rounded-xl bg-slate-100">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Şu anki günlük ciro</span>
                <span className="text-emerald-400 font-bold">₺{predictions?.daily_averages?.revenue?.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-slate-500">Gereken günlük ciro</span>
                <span className={`font-bold ${predictions?.required_daily?.revenue > predictions?.daily_averages?.revenue ? 'text-rose-400' : 'text-emerald-400'}`}>
                  ₺{predictions?.required_daily?.revenue?.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Alerts Tab
export const AlertsTab = ({ dateFrom, dateTo }) => {
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAlerts = async () => {
      setLoading(true);
      try {
        const query = buildDateQuery(dateFrom, dateTo);
        const res = await axios.get(`${API}/analytics/alerts${query}`);
        setAlerts(res.data);
      } catch (error) {
        toast.error('Uyarı verileri yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    fetchAlerts();
  }, [dateFrom, dateTo]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}>
          <Bell size={48} className="text-purple-400" />
        </motion.div>
      </div>
    );
  }

  const getLevelColor = (level) => {
    switch (level) {
      case 'critical': return 'border-red-500/50 bg-red-500/10';
      case 'warning': return 'border-amber-500/50 bg-amber-500/10';
      case 'info': return 'border-blue-500/50 bg-blue-500/10';
      case 'opportunity': return 'border-emerald-500/50 bg-emerald-500/10';
      default: return 'border-slate-300 bg-slate-100';
    }
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'critical': return <AlertCircle className="text-red-400" />;
      case 'warning': return <Bell className="text-amber-400" />;
      case 'info': return <Phone className="text-blue-400" />;
      case 'opportunity': return <TrendingUp className="text-emerald-400" />;
      default: return <Bell className="text-slate-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-red-400">{alerts?.critical_count || 0}</p>
            <p className="text-sm text-slate-500">Kritik Uyarı</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-amber-400">{alerts?.warning_count || 0}</p>
            <p className="text-sm text-slate-500">Dikkat Uyarısı</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-500/10 border-emerald-500/30">
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-emerald-400">{alerts?.total_alerts || 0}</p>
            <p className="text-sm text-slate-500">Toplam Uyarı</p>
          </CardContent>
        </Card>
      </div>

      {/* Alert List */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell size={18} className="text-amber-400" />
            Sistem Uyarıları
          </CardTitle>
        </CardHeader>
        <CardContent>
          {alerts?.alerts?.length > 0 ? (
            <div className="space-y-4">
              {alerts.alerts.map((alert, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className={`p-4 rounded-xl border ${getLevelColor(alert.level)}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-lg bg-slate-200">
                      {getLevelIcon(alert.level)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-slate-800">{alert.title}</h4>
                        <span className="px-2 py-0.5 rounded text-xs bg-slate-200 text-slate-500">
                          Eşik: {alert.threshold}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{alert.message}</p>
                      <p className="text-sm text-slate-500 mt-2">
                        Mevcut değer: <span className="font-bold text-slate-800">{alert.value?.toLocaleString()}</span>
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <CheckCircle size={48} className="mx-auto text-emerald-400 mb-4" />
              <p className="text-slate-500">Tüm metrikler normal seviyelerde!</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Export Tab
export const ExportTab = ({ dateFrom, dateTo }) => {
  const [followUps, setFollowUps] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFollowUps = async () => {
      setLoading(true);
      try {
        const query = buildDateQuery(dateFrom, dateTo);
        const res = await axios.get(`${API}/analytics/export/follow-ups${query}`);
        setFollowUps(res.data);
      } catch (error) {
        toast.error('Takip listesi yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    fetchFollowUps();
  }, [dateFrom, dateTo]);

  const exportToCSV = () => {
    if (!followUps?.data) return;
    
    const headers = ['Müşteri', 'Danışman', 'Durum', 'Not', 'Telefon', 'Tarih'];
    const rows = followUps.data.map(item => [
      item.customer_name || '',
      item.agent_name || '',
      item.status || '',
      (item.note || '').replace(/,/g, ';'),
      item.telefon || '',
      item.timestamp || ''
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `takip_listesi_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    toast.success('CSV dosyası indirildi!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}>
          <Download size={48} className="text-purple-400" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Download size={18} className="text-emerald-400" />
                Takip Listesi Export
              </CardTitle>
              <CardDescription>Düşünecek, Olumlu Arayacak ve İleri Tarih müşterileri</CardDescription>
            </div>
            <Button onClick={exportToCSV} className="bg-emerald-600 hover:bg-emerald-500">
              <Download size={16} className="mr-2" />
              CSV İndir ({followUps?.total || 0} kayıt)
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            <div className="space-y-2">
              {followUps?.data?.slice(0, 50).map((item, i) => (
                <div key={i} className="p-3 rounded-lg bg-slate-100 flex items-center gap-4">
                  <div className="flex-1">
                    <p className="font-medium text-slate-800">{item.customer_name || 'İsimsiz'}</p>
                    <p className="text-sm text-slate-500">{item.note?.slice(0, 80) || 'Not yok'}...</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-emerald-400">{item.status}</p>
                    <p className="text-xs text-slate-500">{item.agent_name}</p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default {
  ChartsTab,
  FunnelTab,
  AgentComparisonTab,
  PredictionsTab,
  AlertsTab,
  ExportTab
};
